import React, { useEffect, useRef, useState } from "react";
import RouteTimeline from "./timeline/index.jsx";
import MapComponent from "./map/MapComponent.jsx";
import styles from "./RouteModal.module.css";

export default function RouteModal({ route, onClose }) {
  // === состояние ===
  const [split, setSplit] = useState(60); // ширина карты в %
  const [selected, setSelected] = useState(null);
  const KEY = "rmSplit";

  // === ссылки ===
  const containerRef = useRef(null);
  const dividerRef = useRef(null);
  const isDragging = useRef(false);
  const grabOffset = useRef(0);

  // === вспомогательные функции ===
  const applySplit = () => {
    if (!containerRef.current) return;
    const { width } = containerRef.current.getBoundingClientRect();
    const minPx = 40;
    const maxPx = Math.max(80, width - 200);
    let leftPx = (split / 100) * width;
    leftPx = Math.min(maxPx, Math.max(minPx, leftPx));
    containerRef.current.style.gridTemplateColumns = `${leftPx}px 6px 1fr`;
  };

  // === загрузка сохранённого значения ===
  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    if (saved) {
      const val = parseFloat(saved);
      if (Number.isFinite(val)) setSplit(val);
    }
  }, []);

  // === применение при изменении split ===
  useEffect(() => {
    applySplit();
  }, [split]);

  // === обновляем при ресайзе окна ===
  useEffect(() => {
    const onResize = () => applySplit();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // === закрытие по ESC ===
  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // === сброс выделения при смене маршрута ===
  useEffect(() => {
    setSelected(null);
  }, [route?.id]);

  // === обработчики drag-разделителя ===
  const onPointerDown = (e) => {
    if (!containerRef.current || !dividerRef.current) return;
    isDragging.current = true;
    dividerRef.current.setPointerCapture?.(e.pointerId);
    const rect = dividerRef.current.getBoundingClientRect();
    grabOffset.current = e.clientX - rect.left;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const onPointerMove = (e) => {
    if (!isDragging.current || !containerRef.current) return;
    const bounds = containerRef.current.getBoundingClientRect();
    let x = e.clientX - bounds.left - grabOffset.current;
    const minPx = 40;
    const maxPx = Math.max(80, bounds.width - 200);
    x = Math.min(Math.max(x, minPx), maxPx);
    containerRef.current.style.gridTemplateColumns = `${x}px 6px 1fr`;
    const percent = (x / bounds.width) * 100;
    setSplit(percent);
  };

  const onPointerUp = (e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    dividerRef.current?.releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    // сохраняем текущее положение точно по DOM
    if (containerRef.current) {
      const bounds = containerRef.current.getBoundingClientRect();
      const cols = getComputedStyle(containerRef.current).gridTemplateColumns.split(" ");
      const leftPx = parseFloat(cols[0]);
      const percent = (leftPx / bounds.width) * 100;
      localStorage.setItem(KEY, String(percent));
      setSplit(percent);
    }
  };

  // === координаты подсветки на карте ===
  const highlightPos = (() => {
    if (!selected) return null;
    const lat = parseFloat(selected.latitude);
    const lng = parseFloat(selected.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  })();

  // === обработчики кликов ===
  const handleMarkerClick = (id) => {
    const clicked = (route?.receipts || []).find(
      (r) => String(r.id) === String(id)
    );
    if (clicked) setSelected(clicked);
  };

  const handleSelectReceipt = (r) => setSelected(r || null);

  // === render ===
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={containerRef}
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={{ gridTemplateColumns: "60% 6px 1fr" }}
      >
        {/* === левая часть — карта === */}
        <div className={styles.mapSection}>
          <MapComponent
            center={[55.751244, 37.618423]}
            zoom={11}
            receipts={route?.receipts || []}
            onMarkerClick={handleMarkerClick}
            selectedReceipt={selected}
            highlightPosition={highlightPos}
            splitPercent={split}
          />
        </div>

        {/* === разделитель === */}
        <div
          ref={dividerRef}
          className={styles.divider}
          role="separator"
          aria-orientation="vertical"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <span className={styles.grip} />
        </div>

        {/* === правая часть — таймлайн === */}
        <div className={styles.timelineSection}>
          <div className={styles.timelineScroll}>
            <RouteTimeline
              route={route}
              selectedReceipt={selected}
              onSelectReceipt={handleSelectReceipt}
              onPersonClick={(name) => console.log("Clicked:", name)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
